<template>
  <div v-if="isMobileMenuOpen" class="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
    @click="isMobileMenuOpen = false" />
  <!-- laptop+: the header is in the page flow until it turns fixed on scroll; keep its height so the page
       doesn't jump up (which pulled scrollY back under 50 and made the header flicker) -->
  <div v-if="isScrolled" class="hidden laptop:block flex-shrink-0" :style="{ height: headerHeight + 'px' }"
    aria-hidden="true" />
  <div ref="headerWrap" :class="[
    ' z-50 transition-all duration-300',
    isScrolled ? 'fixed top-0 bg-white shadow-md' : ' top-0 left-0 laptop:relative'
  ]">
    <header :class="[
      'mainHeader w-full z-50 transition-all duration-300 mobile:!p-0',
      isScrolled
        ? 'fixed top-0 bg-white shadow-md'
        : 'absolute top-0 left-0 laptop:relative !bg-transparent'
    ]">
      <!-- z-[60]: above the search bar below it (z-50), else the Create Quote dropdown opens behind the search -->
      <nav
        class="navbar laptop:h-[88px] mobile:h-auto navbar-expand-lg shadow-md mobile:px-2 largeDesktop:px-5 py-3 px-lg-3 z-[60] mobile:rounded-br-[40px] tablet:rounded-br-[40px] bg-white">
        <div class="container-fluid">
          <!-- Logo: the technician's company logo (Figma 5689-6372), goes to the dashboard. fyxify logo until it loads -->
          <router-link v-if="companyLogo" to="/dashboard" aria-label="Go to dashboard"
            class="flex items-center flex-shrink-0 mr-5 no-underline h-[40px] laptop:h-[52px] max-w-[140px] laptop:max-w-[190px]">
            <img :src="companyLogo" alt="Company Logo" class="h-full w-auto max-w-full object-contain object-left"
              @error="logoFailed = true">
          </router-link>
          <div class="flex items-center gap-2.5 laptop:gap-3 px-[0.5rem]">
            <!-- below laptop: Create Quote next to the search icon (Teleport target, see globaldashboardHead.vue) -->
            <div v-show="!isMobileMenuOpen" id="headerCreateQuoteMobile" class="laptop:hidden flex items-center" />
            <!-- search button for mobile  -->
            <button v-if="!isMobileMenuOpen" ref="searchButton"
              class="mobile:block laptop:hidden largeDesktop:hidden w-8 h-8 " type="button"
              data-bs-target="#globalSearch" data-bs-toggle="collapse">
              <img :src="searchOpen ? crossIcon : searchIcon" alt="searchicon" class="w-full">
            </button>

            <!-- Toggle Button for Mobile -->
            <button class="navbar-toggler focus:!outline-none focus:!shadow-none collapsed p-0 border-0" type="button"
              aria-label="Toggle navigation" @click="isMobileMenuOpen = !isMobileMenuOpen">
              <!-- <span class="navbar-toggler-icon"></span> -->
              <img v-if="!isMobileMenuOpen" src="../../../assets/images/hamburger-icon.png" alt="Hamburger Icon"
                class="w-8 h-8 object-contain">
              <img v-else src="../../../assets/images/hamburger-times-icon.png" alt="Hamburger Icon"
                class="w-8 h-6 object-contain">
            </button>
          </div>
          <!-- Navbar Content -->
          <div :class="[
            'navbar-collapse mobile:!m-0 laptop:block transition-all duration-300 ease-in-out !ml-5 laptop:min-h-auto laptop:max-h-auto overflow-y-auto custom-scrollbar mobile:max-h-[70vh] mobile:pr-2 tablet:pr-0 tablet:max-h-[auto] px-[0.5rem]',
            isMobileMenuOpen ? 'block bg-white' : 'hidden '
          ]">
            <div class="mobile:flex mobile:flex-col-reverse laptop:flex-row items-center justify-between w-full ">
              <div class="mobile:flex laptop:hidden largeDesktop:hidden items-start gap-5 w-full">
                <sidebarNavigation :set-side-menu-clicked="sideMenuClicked" />
                <!-- <sidebar/> -->
              </div>

              <!-- Dashboard / How fyxify works / Contact Us moved to the footer (user, 20 Oct); the logo still opens
                   the dashboard -->
              <div class="mobile:hidden laptop:block flex-1" />

              <!-- Right Side Nav -->
              <!-- below laptop (inside the menu) the pills wrap to a new line instead of squeezing -->
              <div
                class="flex items-center justify-end mobile:gap-2 mobile:py-0 laptop:w-1/2 laptop:gap-3 w-full [&>*]:flex-shrink-0">
                <!-- Create Quote (rendered here from globaldashboardHead.vue via Teleport) -->
                <div id="headerCreateQuote" class="flex items-center" />
                <headerMessage
                  :buttonClass="'inline-flex justify-center items-center mobile:w-[38px] laptop:w-auto h-[38px] laptop:h-[42px] headerMsgBtn rounded-full border font-medium focus:outline-none mobile:px-0 mobile:text-sm laptop:text-base largeDesktop:text-base mobile:gap-2 laptop:px-3 laptop:gap-3 text-gray-800 !border-gray-100 bg-gray-100 hover:bg-white no-underline'" />

                <!-- Notification Button -->
                <div class="relative inline-block text-left">
                  <button
                    class="relative inline-flex justify-center items-center mobile:w-[38px] laptop:w-auto h-[38px] laptop:h-[42px] rounded-full border font-medium hover:bg-white focus:outline-none mobile:px-0 mobile:text-sm laptop:text-base largeDesktop:text-base mobile:gap-2 laptop:px-3 laptop:gap-3 border-gray-100 bg-gray-100 text-gray-800"
                    @click="notificationModal()">
                    <img src="../../../assets/images/header-notification-icon.png" alt="Notification Icon"
                      class="laptop:w-5 laptop:h-5 mobile:w-5 mobile:h-5 object-contain">
                    <!-- web (laptop+): label then "• count"; below laptop: round icon button, count as a round badge
                         (mobile:hidden alone hid the label everywhere: the mobile breakpoint starts at 320px) -->
                    <span class="whitespace-nowrap mobile:hidden laptop:inline">Notifications</span>
                    <div v-if="notificationCount > 0" class="mobile:hidden laptop:flex gap-1 items-center">
                      <span class="inline-block w-1 h-1 rounded-full bg-orange-100" />
                      <span class="inline-block">{{ notificationCount }}</span>
                    </div>
                    <span v-if="notificationCount > 0"
                      class="laptop:hidden absolute -top-[4px] -right-[4px] min-w-[18px] h-[18px] px-[4px] rounded-full bg-[#F67280] text-white text-[11px] leading-[18px] text-center">
                      {{ notificationCount > 99 ? '99+' : notificationCount }}</span>
                  </button>
                </div>

                <!-- online offline Switch -->
                <switchSlider :user="user" @toggle="handleStatusChange" />

                <!-- Profile Button -->
                <div class="relative mobile:w-11 mobile:h-11 largeDesktop:w-14 largeDesktop:h-14 flex items-center">
                  <button class="inline-flex justify-center items-center" @click="profileModal()">
                    <img :src="(user && user.user.profile_picture) || defaultAvatar" alt="User Profile Icon"
                      class="mobile:w-11 mobile:h-11 largeDesktop:w-14 largeDesktop:h-14 object-cover rounded-full">
                  </button>
                </div>
              </div>

              <div class="mobile:order-first laptop:hidden">
                <button type="button"
                  class="mobile:py-1.5 mobile:px-5 tablet:py-1.5 tablet:px-4 largeDesktop:py-2 largeDesktop:px-4 mt-2 mobile:mx-2 tablet:mx-4 mobile:text-base tablet:text-base largeDesktop:text-lg rounded-full no-underline w-max !min-w-[150px] text-[##333333] bg-[#F1F1F1] border-[1px] border-[#E3E3E3]"
                  @click="signoutClicked">
                  Sign
                  Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <!-- search bar  -->
      <div v-if="!isMobileMenuOpen" id="globalSearch" class="navbar-collapse collapse relative z-50">
        <div class="w-full pt-3 px-2 ">
          <globalSearch v-model="search"
            :input-class="'rounded-full shadow-md pl-3 pr-20 py-3 w-full largeDesktop:h-11 bg-white disabled:!bg-white disabled:opacity-100 focus:outline-none'"
            :buttonclass="'absolute top-[12px] right-2 w-8 h-8'"
            :clear-class="'absolute top-0 flex items-center justify-center !h-[100%] right-[3.2rem] laptop:w-4 laptop:h-4 largeDesktop:w-6 largeDesktop:h-6'"
            :clearable="true" :on-enter="handleSearch" :on-clear="handleClear" :readonly="loading" />
        </div>
      </div>
    </header>
  </div>

  <!-- Profile Modal -->
  <div id="profileModal" ref="profileModal" class="modal fade" tabindex="-1" aria-labelledby="profileModal"
    aria-hidden="true">
    <div
      class="modal-dialog modal-dialog-centered !font-roboto mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:!w-[500px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
      <div class="modal-content select-none">
        <div class="modal-body !p-3 !pb-5">
          <div class="relative ">
            <button type="button"
              class="absolute top-3 right-4 w-[20px] h-[20px] z-50 focus:!outline-none focus:!shadow-none"
              data-bs-dismiss="modal" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="none" stroke="#ff0004" stroke-linecap="round" stroke-width="2" d="M20 20L4 4m16 0L4 20" />
              </svg>

            </button>
          </div>
          <div class="">
            <headerUserProfile :open-modal="profileModalOpen" :bootme="bootMeUpData" :clicked-callback="clickedCallback"
              :user-object="user" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Notification Modal -->
  <headerNotification :open-popup="openNotificationPopup" @closed="refreshUnreadCount" />
</template>

<script>
import { TECH_HOME_ROUTE } from "@/helpers/techMenu";
import { Modal } from "bootstrap";
import {
  mapGetters, mapActions
} from "vuex";
import headerMessage from "../../nest_components/header_message.vue";
import headerNotification from "../../nest_components/header_notification.vue";
import { notificationApi } from "../../../services/notificationApi";
import { loadOnce } from "@/helpers/loadOnce";
import { disableScroll, enableScroll } from "@/helpers/scrollHelper";
import headerUserProfile from "../../nest_components/header_profile.vue";
import switchSlider from "../../partialComponent/customSwitchSlider.vue";
import globalSearch from '../../partialComponent/customInput.vue';
import sidebarNavigation from '../../../components/sidebarNavigation.vue';
import showIcon from '../../../assets/images/searchBar-icon.png';
import fyxifyLogo from '../../../assets/images/fyxifyProLogo.png'; // Android PRO app icon
import hideIcon from '../../../assets/images/crossIcon.png';
// import sidebar from "../../nest_components/omega_sidebar/sidebar.vue";
export default {
  components: {
    headerMessage,
    headerNotification,
    headerUserProfile,
    switchSlider,
    globalSearch,
    sidebarNavigation,
    // sidebar
  },
  data() {
    return {
      homeRoute: TECH_HOME_ROUTE,
      headerHeight: 0, // in-flow height, kept as a spacer while the header is fixed
      loading: false,
      search: '',
      isScrolled: false,
      searchIcon: showIcon,
      crossIcon: hideIcon,
      searchOpen: false,
      logoFailed: false,
      // empty profile_picture (removed in Edit Profile) → same default avatar as Edit Profile
      defaultAvatar: 'https://t3.ftcdn.net/jpg/03/46/83/96/360_F_346839683_6nAPzbhpSkIpb8pmAwufkC7c5eD7wYws.jpg',
      isMobileMenuOpen: false, // ← Add this
      profileModalOpen: new Date(),
      user: JSON.parse(localStorage.getItem('user')) ?? null,
      openNotificationPopup: new Date(),
      unreadTimer: null
    };
  },
  computed: {
    ...mapGetters(["setCompanyInfo", "saveCompanyInfo", "maintenanceBannerData", "bootMeUpData", "userObject", "updateStatusResponse", "signOutResponse", "notificationCountResponse"]),
    companyLogo() {
      const logo = this.user?.user?.company_logo;
      return (!logo || this.logoFailed) ? null : logo;
    },
    notificationCount() {
      return this.notificationCountResponse ? this.notificationCountResponse.unread : 0
    }
  },

  watch: {
    // responsive menu: any navigation from it (Dashboard, a submenu item…) closes it
    "$route.fullPath"() {
      this.isMobileMenuOpen = false;
    },
    // lock the page behind the mobile menu with the shared helpers: they also pin left: 0 (without it the fixed body
    // was pushed right by eval-figma's `right: var(--page-scrollbar)` and the menu started mid-screen) and keep the scroll
    isMobileMenuOpen(newVal) {
      if (newVal) {
        disableScroll();
      } else {
        enableScroll();
        this.searchOpen = false;
      }
    },

    updateStatusResponse: function () {
      this.getProfile({})
    },

    userObject: function () {
      this.user = this.userObject
      localStorage.setItem('user', JSON.stringify(this.user))
    },

    bootMeUpData: function () { },

    signOutResponse: function (val) {
      if (val) {
        localStorage.removeItem('user')
        localStorage.removeItem('isSilentLogin')
        localStorage.removeItem('isProtectionPlans')
        localStorage.removeItem('is_from_mobile')
        sessionStorage.removeItem('dashboard_selected_date') // dashboard opens on today after the next login
        this.resetSate()
        this.$router.replace({
          name: 'login'
        })
      }
    },

    '$route.query': {
      immediate: true,
      handler(newQuery, oldQuery) {
        if (newQuery) {
          this.search = this.$route.query.q
        }

        // open the mobile search bar for a new search only, not when the results page changes its own query
        // (type filter / page / entries), else it popped open again after the user closed it
        const newSearch = !oldQuery || newQuery?.q !== oldQuery?.q || newQuery?.t !== oldQuery?.t;
        if (this.$route.name == 'globalSearch' && newSearch) {
          this.$nextTick(() => {
            const isMobile = window.innerWidth < 1024
            const isCollapsed = !document.getElementById('globalSearch')?.classList.contains('show');

            if (isMobile && isCollapsed) {
              this.$refs.searchButton.click();
            }
          });
        }
      },
    },
  },

  mounted() {
    const is_from_mobile = localStorage.getItem("is_from_mobile", 0) ?? 0
    if (is_from_mobile != '1') {
      loadOnce('profile', () => this.getProfile({}))
      this.getBootMeData({})
    }
    window.addEventListener("scroll", this.handleScroll);
    document.addEventListener("show.bs.collapse", this.onSearchCollapse);
    document.addEventListener("hide.bs.collapse", this.onSearchCollapse);
    // only x: a full 'hidden' became the page's overflow whenever a modal reset <html> overflow to '' → page stuck
    // clip, not hidden: hidden turns <body> into a scroll box and every position:sticky inside stops sticking
    document.body.style.overflowX = 'clip';
    // unread badge: on mount, then every 60s (Android refreshes it on dashboard resume)
    this.refreshUnreadCount()
    this.unreadTimer = setInterval(this.refreshUnreadCount, 60000)
  },
  beforeUnmount() {
    clearInterval(this.unreadTimer)
    this.unreadTimer = null
    document.body.style.overflowX = '';
    window.removeEventListener("scroll", this.handleScroll);
    document.removeEventListener("show.bs.collapse", this.onSearchCollapse);
    document.removeEventListener("hide.bs.collapse", this.onSearchCollapse);
    document.body.style.overflowX = '';
  },
  methods: {
    ...mapActions(["getCompanyInfo", "getMaintenanceBannerData", "getBootMeData", "updateStatus", "getProfile", "signOut", "resetSate", "unreadNotificationsCount"]),
    adjustModalHeight() {
      const modalContent = this.$refs.profileModal?.querySelector('.modal-content');
      if (modalContent) {
        const vh = window.innerHeight;
        modalContent.style.maxHeight = `${vh - 40}px`; // adjust padding as needed
        modalContent.style.overflowY = 'auto';
      }
    },

    clickedCallback(option) {
      if (option == 'signout') {
        this.signoutClicked()
      }
    },
    signoutClicked() {
      const originalPadding = document.documentElement.style.padding || '';
      // Remove padding from HTML element
      document.documentElement.style.padding = '0';
      document.documentElement.style.paddingRight = '0';
      this.$confirmDialog.show({
        image: 'logout-confirm-icon.svg',
        title: "Sign Out",
        text: "Are you sure you want to sign out?",
        buttonText: "Confirm",
        isShowConfirmText: true,
        // image: "pop-req-img.png",
        // we are passing callback method for our confirm button
        onConfirm: () => {
          this.signOut({})
        },
      });
    },

    notificationModal() {
      this.openNotificationPopup = new Date()
    },

    // silent background refresh; the list call (notifications/list) marks everything read server-side
    async refreshUnreadCount() {
      if (!localStorage.getItem('user')) return
      const res = await notificationApi.unreadCount()
      if (res.ok && res.body) {
        this.$store.commit("storeNotificationCountResponse", { unread: Number(res.body.unread) || 0 })
      }
    },

    sideMenuClicked() {
      this.isMobileMenuOpen = false
    },
    handleNavClick(item) {
      this.isMobileMenuOpen = false;
      console.log(`Navigating to: ${item.link}`); // item is an object now

      // Collapse any open Bootstrap navbars
      const collapseElements = document.querySelectorAll('.navbar-collapse');
      collapseElements.forEach(el => {
        if (el.classList.contains('show')) {
          new this.Collapse(el).hide();
        }
      });

      // Route to the correct page
      this.$router.push(item.link);
    },
    handleScroll() {
      // a modal / the mobile menu locked the page (body fixed → scrollY 0): keep the header as it is, else it
      // unpinned and moved up with the page, taking the opened menu off screen
      if (document.body.style.position === "fixed") return;
      const scrolled = window.scrollY > 50;
      if (scrolled && !this.isScrolled) this.headerHeight = this.$refs.headerWrap?.offsetHeight || 0;
      this.isScrolled = scrolled;
    },

    async handleStatusChange(isOnline, done) {
      const ok = await this.updateStatus({
        active: isOnline ? '1' : '0',
        lat: '0',
        long: '0',
      })
      if (done) done(ok !== false)
    },
    // icon follows the real Bootstrap collapse state (a blind toggle drifted on fast / programmatic clicks)
    onSearchCollapse(e) {
      if (e.target?.id !== 'globalSearch') return
      this.searchOpen = e.type === 'show.bs.collapse'
    },
    profileModal() {
      const { Modal } = require('bootstrap');
      const modalEl = this.$refs.profileModal;
      this.sideMenuClicked()
      let modal = Modal.getInstance(modalEl);
      if (!modal) {
        modal = new Modal(modalEl, {
          backdrop: 'static',
          keyboard: false
        });
      }
      // Add event listeners for show/hide
      modalEl.addEventListener('show.bs.modal', () => {
        document.documentElement.style.overflow = 'hidden'; // Target HTML element
        if (window.innerWidth >= 992) {
          document.documentElement.style.paddingRight = '15px';
        } else {
          document.documentElement.style.paddingRight = '0px';
        }
        this.adjustModalHeight();
      });
      modalEl.addEventListener('hidden.bs.modal', () => {
        document.documentElement.style.overflow = ''; // Reset HTML element
        document.documentElement.style.paddingRight = '0px';
        window.removeEventListener('resize', this.adjustModalHeight);
        window.removeEventListener('orientationchange', this.adjustModalHeight);
      });

      this.profileModalOpen = new Date()
      // Add orientation/resize responsiveness
      window.addEventListener('resize', this.adjustModalHeight);
      window.addEventListener('orientationchange', this.adjustModalHeight);
      modal.show();
    },
    backButton() {
      this.isMobileMenuOpen = false
      this.$router.go(-1);
    },
    setLoading() {
      this.loading = true
      setTimeout(() => {
        this.loading = false
      }, 200);
    },
    handleClear() {
      this.search = ''
      this.handleSearch()
    },
    handleSearch() {
      let search = ""
      search = this.$helper.isEmpty(this.search) ? '' : this.search.trim()
      this.setLoading()
      if (this.$route.name == 'globalSearch') {
        this.$router.replace({
          name: 'globalSearch',
          query: {
            q: search,
            t: Date.now()
          },
        });
      } else {
        this.$router.push({
          name: 'globalSearch',
          query: {
            q: search,
            t: Date.now()
          },
        });
      }
    }
  }
};
</script>
<style>
@media (max-width: 768px) {

  .collapse:not(.show) {
    display: none;
  }

  .collapse.show {
    display: block;
    height: auto;
    visibility: visible;
  }
}

@media (min-width: 768px) {

  .collapse:not(.show) {
    display: none
  }

  .collapse.show {
    display: block;
    height: auto;
    visibility: visible;
  }
}

a.router-link-active {
  border-bottom: 0 !important;
}

/* Messages pill keeps its full border on /messages (the rule above would drop the bottom edge) */
a.router-link-active.headerMsgBtn {
  border: 1px solid #dee2e6 !important;
}

/* beats the global a.router-link-active { color: #000 !important } */
a.nav-pill-active,
a.router-link-active.nav-pill-active {
  color: #fff !important;
}
</style>